/**
 * CourseCard Component
 *
 * Card component for displaying course information in grid layout.
 * Handles course enrollment status, progress, and actions (Enroll/Continue/View).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Course, EnrollmentStatus, CourseType } from '../../apis/api-client';
import { AssignmentStatusBadge } from '../common';
import { Box, Button, Card, LinearProgress, Typography } from '../ui';
import { BookmarkIcon, ChevronRightIcon, PlayCircleIcon, SchoolIcon } from '../icons';
import { courseTypeMeta } from './contentTypeMeta';
import { useSelector } from 'react-redux';
import { RootState } from '../../types';
import { selectAuthorById, useEnsureAuthors } from '../../features/lookups/lookupsSlice';

interface CourseCardProps {
  course: Course;
  onActionClick: () => void;
  isAuthenticated?: boolean;
  sx?: any;
}

// Type-coded palette for the corner pill — matches the curriculum
// builder + create-menu + activity panel so a Learning Path card always
// looks like a Learning Path no matter which surface renders it.
const CourseCard: React.FC<CourseCardProps> = ({ course, onActionClick, isAuthenticated = true, sx }) => {
  const { title, isDraft } = course;
  // Resolve the instructor name from the shared author directory.
  useEnsureAuthors();
  const description = course.description;
  const thumbnailUrl = (course as any).thumbnailUrl || course.thumbnail;
  const enrollmentStatus = course.currentUserEnrollment?.enrollmentStatus;
  const progressPercentage = course.currentUserEnrollment?.progressPercentage;
  const isAssigned = course.currentUserEnrollment?.isAssigned;
  const assignmentStatus = course.currentUserEnrollment?.assignmentStatus;
  const deadline = course.currentUserEnrollment?.deadline;
  const courseType: CourseType | undefined = (course as any).courseType;
  const isExternal = courseType === CourseType.External;
  const isLearningPath = courseType === CourseType.LearningPath;
  const eventDate = (course as any).eventDate;
  const totalPathCourses = (course as any).totalPathCourses || 0;
  const completedPathCourses = (course as any).completedPathCourses || 0;

  const typeMeta = courseTypeMeta(courseType, 13);
  // CE hours awarded — a scalar on the course record. Surfaced as a
  // small badge so learners can weigh credit at a glance without
  // opening the detail page.
  const ceHours = course.ceHours;
  const instructor = useSelector((s: RootState) => selectAuthorById(s, course.authorId));
  const hasEnrollmentRecord = !!course.currentUserEnrollment?.enrollmentId;
  const isInProgress =
    hasEnrollmentRecord &&
    enrollmentStatus !== undefined &&
    enrollmentStatus !== EnrollmentStatus.NotStarted &&
    enrollmentStatus !== EnrollmentStatus.Completed;
  const isCompleted = enrollmentStatus === EnrollmentStatus.Completed;
  const progressPct = isCompleted
    ? 100
    : Math.max(0, Math.min(100, Math.round(progressPercentage || 0)));

  const navigate = useNavigate();

  const handleCardClick = () => {
    // Always use slug-based URL for all users
    navigate(`/courses/${(course as any).slug || course.id}`);
  };

  const handleLearnMore = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Same destination as the card click — an explicit button so users who
    // want the marketing/details view don't have to accidentally trigger the
    // primary Enroll action when they only meant to click through.
    navigate(`/courses/${(course as any).slug || course.id}`);
  };

  // Two-state CTA. Anything without an enrollment record is a "Learn
  // more" — clicks navigate to the course page where the learner can
  // read the marketing detail and enroll deliberately. Anything they
  // already have an enrollment for is "Continue" — clicks call the
  // parent's primary action (resume / open).
  const isEnrolled = enrollmentStatus !== undefined;

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onActionClick();
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
          borderColor: 'primary.main',
        },
        '&:hover img': {
          transform: 'scale(1.05)',
        },
        '&:hover .title': {
          color: 'primary.main',
        },
        cursor: 'pointer',
        height: '100%',
        ...sx,
      }}
      onClick={handleCardClick}
    >
      {/* Thumbnail with corner overlays — type chip top-left, assigned
          badge top-right. Progress bar (when applicable) hugs the
          bottom edge of the image so it reads as an attribute of the
          card's hero, not a stray pill in the body. */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          // Slightly shorter than 16:9 (≈11% less height at the same
          // width) so the hero band feels like a banner crop rather
          // than a full video frame. With `cover` below, 16:9 source
          // graphics still read edge-to-edge — just trimmed a thin
          // band off the top/bottom.
          aspectRatio: '2 / 1',
          overflow: 'hidden',
          bgcolor: 'grey.50',
        }}
      >
        {thumbnailUrl ? (
          <Box
            component="img"
            src={thumbnailUrl}
            alt={title}
            sx={{
              width: '100%',
              height: '100%',
              // `cover` so the artwork always fills the band — no
              // letterbox grey gutters when sources aren't an exact
              // match. Acceptable trim for the catalog grid where
              // consistent card heights matter more than seeing
              // every pixel of every thumbnail.
              objectFit: 'cover',
              objectPosition: 'center',
              display: 'block',
              transition: 'transform 0.3s ease',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
              fontSize: 48,
              opacity: 0.4,
            }}
          >
            {typeMeta.icon}
          </Box>
        )}

        {/* Assigned / Draft pill — top-right. Mutually exclusive. */}
        {(isAuthenticated && isAssigned) || isDraft ? (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              px: 0.9,
              py: 0.35,
              borderRadius: 999,
              bgcolor: isDraft ? 'rgba(255,255,255,0.96)' : 'warning.softBg',
              color: isDraft ? 'text.secondary' : 'warning.softText',
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            }}
          >
            {isDraft ? 'Draft' : 'Assigned'}
          </Box>
        ) : null}

        {/* Progress bar pinned to the bottom of the thumbnail. Only when
            the learner has a real enrollment record and has started. */}
        {isAuthenticated && (isInProgress || isCompleted) && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255,255,255,0.85)',
              backdropFilter: 'blur(4px)',
              px: 1.25,
              py: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                sx={{
                  flex: 1,
                  height: 5,
                  borderRadius: 999,
                  bgcolor: 'rgba(0,0,0,0.08)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    ...(isCompleted
                      ? { bgcolor: 'success.main' }
                      : {
                          background:
                            'var(--brand-gradient)',
                        }),
                  },
                }}
              />
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  color: isCompleted ? 'success.main' : 'primary.main',
                  fontSize: '0.7rem',
                  minWidth: 30,
                  textAlign: 'right',
                }}
              >
                {isCompleted ? '✓' : `${progressPct}%`}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: '1',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
        }}
      >
        {/* Title — h6, two-line clamp so off-length titles don't pull
            the grid out of alignment. */}
        <Typography
          variant="subtitle1"
          fontWeight={700}
          className="title"
          sx={{
            transition: 'color 0.3s ease',
            textAlign: 'left',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '2.6em',
          }}
        >
          {title}
        </Typography>

        {/* Event date — primary affordance on Event cards. */}
        {isExternal && eventDate && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.4,
              borderRadius: 1,
              bgcolor: 'rgba(46,125,50,0.10)',
              color: 'success.main',
              fontSize: '0.7rem',
              fontWeight: 700,
              width: 'fit-content',
            }}
          >
            {new Date(eventDate).toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Box>
        )}

        {/* Meta row — CE hours · modules count · instructor. Surfaces
            the facts learners actually use to pick a course without
            them having to drill into the detail page. */}
        {(ceHours != null || isLearningPath || instructor) && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              color: 'text.secondary',
              fontSize: '0.72rem',
            }}
          >
            {ceHours != null && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                <PlayCircleIcon sx={{ fontSize: 14 }} />
                <strong style={{ fontWeight: 700 }}>{ceHours}</strong>&nbsp;CE
              </Box>
            )}
            {isLearningPath && totalPathCourses > 0 && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                <BookmarkIcon sx={{ fontSize: 14 }} />
                {isAuthenticated && enrollmentStatus !== undefined ? (
                  <>
                    <strong style={{ fontWeight: 700 }}>{completedPathCourses}/{totalPathCourses}</strong>&nbsp;courses
                  </>
                ) : (
                  <>
                    <strong style={{ fontWeight: 700 }}>{totalPathCourses}</strong>&nbsp;courses
                  </>
                )}
              </Box>
            )}
            {instructor?.name && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  minWidth: 0,
                }}
              >
                <SchoolIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                <span
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  Instructed by {instructor.name}
                </span>
              </Box>
            )}
          </Box>
        )}

        {/* Description — clamped to 2 lines for consistent card height.
            Reserves space even when empty so the action footer aligns
            across the grid. Hidden for the in-progress / completed
            view since the learner already knows the course. */}
        {(!isAuthenticated || !hasEnrollmentRecord || (!isInProgress && !isCompleted)) && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: 'left',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.6em',
              lineHeight: 1.3,
            }}
          >
            {description || ''}
          </Typography>
        )}

        {/* Assignment status badge — kept as-is, surfaces the deadline. */}
        {isAuthenticated && isAssigned && assignmentStatus !== undefined && (
          <AssignmentStatusBadge assignmentStatus={assignmentStatus} deadline={deadline} />
        )}

        {/* Action footer — single full-width gradient CTA. Two states:
              • Not enrolled (or signed-out) → "Learn more" + chevron,
                navigates to the course detail page.
              • Enrolled → "Continue" + play icon, fires the parent's
                primary action (resume / open).
            Draft is the admin escape hatch; it still routes to the
            edit/detail page. */}
        <Box sx={{ mt: 'auto', pt: 0.5 }}>
          {isDraft ? (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleLearnMore}
              fullWidth
              startIcon={<ChevronRightIcon />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Open Draft
            </Button>
          ) : !isAuthenticated || !isEnrolled ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleLearnMore}
              fullWidth
              endIcon={<ChevronRightIcon />}
              sx={{
                color: '#fff',
                '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Learn more
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleActionClick}
              fullWidth
              startIcon={<PlayCircleIcon />}
              sx={{
                color: '#fff',
                '&.Mui-disabled': { color: '#fff', opacity: 0.65 },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Continue
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default CourseCard;
